import {THREE} from '../base/three-defs.js';

import * as entity from '../base/entity.js';


const AXIS_Y = new THREE.Vector3(0, 1, 0);
const ROT_TMP = new THREE.Quaternion();
const FORWARD_TMP = new THREE.Vector3();
const LEFT_TMP = new THREE.Vector3();
const POS_TMP = new THREE.Vector3();

// Movement tuning ported from surreal-meadow: Source/Quake-style ground
// friction + acceleration toward a wish direction, a lower uncapped air
// accelerate (so strafe-jumping can bank speed), a buffered jump, and
// simple ballistic gravity against the terrain heightfield.
const LOOK_SPEED = 2.2;                      // rad/s at full look stick
const PITCH_LOOK_SPEED = LOOK_SPEED * 0.25;  // (pitch applied in player-camera.js)
const MOUSE_SENSITIVITY = 0.0022;
const MOVE_SPEED = 6.0;
const MAX_AIR_WISH_SPEED = MOVE_SPEED * 1.5; // air-strafe target speed cap (lets bunny-hopping gain speed)
const MAX_SPEED = MOVE_SPEED * 3;            // hard cap on horizontal speed
const GROUND_ACCEL = 14;
const AIR_ACCEL = 16;
const GROUND_FRICTION = 9;
const GRAVITY = -20;
const JUMP_SPEED = 7;
const JUMP_BUFFER_TIME = 0.15; // seconds a jump press is "remembered" while airborne, so a press just before landing still triggers

// Player origin rides this far above the terrain surface; the camera adds
// its own eye height on top (player-camera.js).
const GROUND_OFFSET = 1.5;

// Source/Quake-style acceleration toward a wish direction, used for both
// ground movement and (capped) air strafing.
function accelerate(vel, wishX, wishZ, wishSpeed, accel, dt) {
  const currentSpeed = vel.x * wishX + vel.z * wishZ;
  const addSpeed = wishSpeed - currentSpeed;
  if (addSpeed <= 0) return;
  let accelSpeed = accel * wishSpeed * dt;
  if (accelSpeed > addSpeed) accelSpeed = addSpeed;
  vel.x += accelSpeed * wishX;
  vel.z += accelSpeed * wishZ;
}

function applyGroundFriction(vel, friction, dt) {
  const speed = Math.hypot(vel.x, vel.z);
  if (speed < 0.01) {
    vel.x = 0;
    vel.z = 0;
    return;
  }
  const drop = speed * friction * dt;
  const scale = Math.max(speed - drop, 0) / speed;
  vel.x *= scale;
  vel.z *= scale;
}

export const player_entity = (() => {

  class BasicCharacterController extends entity.Component {
    static CLASS_NAME = 'BasicCharacterController';

    get NAME() {
      return BasicCharacterController.CLASS_NAME;
    }

    constructor(params) {
      super();
      this.params_ = params;
    }

    InitEntity() {
      this.Init_();
    }

    Init_() {
      // World-space horizontal velocity; vertical is tracked separately.
      this.velocity_ = new THREE.Vector3(0, 0, 0);
      this.verticalVel_ = 0;
      this.grounded_ = true;
      this.jumpBufferTimer_ = 0;

      this.yaw_ = null;
      this.smoothYaw_ = null;

      this.group_ = new THREE.Group();

      const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');
      threejs.AddSceneObject(this.group_, {type: 'player'});
    }

    InitComponent() {
      this.RegisterHandler_(
          'update.position', (m) => { this.OnUpdatePosition_(m); });
      this.RegisterHandler_(
          'update.rotation', (m) => { this.OnUpdateRotation_(m); });
    }

    OnUpdatePosition_(msg) {
      this.group_.position.copy(msg.value);
    }

    OnUpdateRotation_(msg) {
      this.group_.quaternion.copy(msg.value);
    }

    Update(dt) {
      const input = this.GetComponent('PlayerInput');

      if (this.yaw_ === null) {
        const q = this.Parent.Quaternion;
        this.yaw_ = 2.0 * Math.atan2(q.y, q.w);
        this.smoothYaw_ = this.yaw_;
      }

      // Yaw: pointer-lock mouse is direct pixel deltas; the look stick /
      // gamepad turn at a rate. Both glide through the same smoothing
      // (matches LOOK_SMOOTHING_TAU in player-camera.js).
      this.yaw_ -= input.current_.mouseDeltaX * MOUSE_SENSITIVITY;
      this.yaw_ -= input.current_.lookX * LOOK_SPEED * dt;

      const smoothT = 1.0 - Math.exp(-dt / 0.06);
      this.smoothYaw_ += (this.yaw_ - this.smoothYaw_) * smoothT;

      const _R = ROT_TMP.setFromAxisAngle(AXIS_Y, this.smoothYaw_);

      const forward = FORWARD_TMP.set(0, 0, 1).applyQuaternion(_R);
      forward.y = 0;
      forward.normalize();
      const left = LEFT_TMP.set(1, 0, 0).applyQuaternion(_R);
      left.y = 0;
      left.normalize();

      // Desired horizontal direction/speed from input, relative to facing.
      // moveX is right(+)/left(-), so it rides the left vector negated.
      const moveX = input.current_.moveX;
      const moveY = input.current_.moveY;
      const wishDirX = forward.x * moveY - left.x * moveX;
      const wishDirZ = forward.z * moveY - left.z * moveX;
      const wishLen = Math.hypot(wishDirX, wishDirZ);
      const wishSpeed = Math.min(wishLen, 1) * MOVE_SPEED;
      const normX = wishLen > 0.0001 ? wishDirX / wishLen : 0;
      const normZ = wishLen > 0.0001 ? wishDirZ / wishLen : 0;

      const velocity = this.velocity_;
      if (this.grounded_) {
        applyGroundFriction(velocity, GROUND_FRICTION, dt);
        accelerate(velocity, normX, normZ, wishSpeed, GROUND_ACCEL, dt);
      } else {
        // Lower cap + no friction in the air lets strafe-jumping carry
        // more speed than ground movement alone (bunny-hop tech).
        accelerate(velocity, normX, normZ,
            Math.min(wishSpeed, MAX_AIR_WISH_SPEED), AIR_ACCEL, dt);
      }

      const speed = Math.hypot(velocity.x, velocity.z);
      if (speed > MAX_SPEED) {
        const scale = MAX_SPEED / speed;
        velocity.x *= scale;
        velocity.z *= scale;
      }

      const pos = POS_TMP.copy(this.Parent.Position);
      pos.x += velocity.x * dt;
      pos.z += velocity.z * dt;

      if (input.ConsumeJump()) {
        this.jumpBufferTimer_ = JUMP_BUFFER_TIME;
      }

      const terrain = this.FindEntity('terrain');
      if (terrain) {
        const terrainComponent = terrain.GetComponent('TerrainComponent');
        if (terrainComponent.IsReady()) {
          const groundY = terrainComponent.GetHeight(pos.x, pos.z) + GROUND_OFFSET;

          if (this.jumpBufferTimer_ > 0) {
            if (this.grounded_) {
              this.verticalVel_ = JUMP_SPEED;
              this.grounded_ = false;
              this.jumpBufferTimer_ = 0;
            } else {
              this.jumpBufferTimer_ -= dt;
            }
          }

          if (this.grounded_) {
            pos.y = groundY;
          } else {
            this.verticalVel_ += GRAVITY * dt;
            pos.y += this.verticalVel_ * dt;
            if (pos.y <= groundY) {
              pos.y = groundY;
              this.verticalVel_ = 0;
              this.grounded_ = true;
            }
          }
        }
      }

      this.Parent.SetPosition(pos);
      this.Parent.SetQuaternion(_R);
    }
  };

  return {
    BasicCharacterController: BasicCharacterController,
    PITCH_LOOK_SPEED: PITCH_LOOK_SPEED,
    MOUSE_SENSITIVITY: MOUSE_SENSITIVITY,
  };

})();
